// Sprite dei marker stazione — port 1:1 di buildStationGlSprite (map-markers.js,
// motore ibrido v1): stessa geometria (r 17/20/23, badge estremi, pillola quota,
// freccia vento, font Inter 760). La v1 resta la fonte di verità per la resa:
// non divergere. Differenza SOLO architetturale: niente letture di DOM/globali,
// tutte le opzioni arrivano per parametro.
// Globali richiesti: chroma (vendor), window.WSPalettes (map-palettes.js v1).

let exportDpr = 0;
export function setStationSpriteExportDpr(value = 0) {
    exportDpr = Number.isFinite(value) && value > 0 ? Math.min(4, value) : 0;
}
export function stationSpriteDpr() {
    return exportDpr || Math.max(1, Math.min(window.devicePixelRatio || 1, 2.5));
}

// Override della dimensione marker (?markersize=full|mid|low e ?markerscale=N).
// Nessun consumatore normale li passa: servono agli screenshot "social" del bot,
// dove in vista Sicilia la fascia automatica scende a 'low' (r 17, font 10.5) e
// dopo la ricompressione di Telegram i valori diventano illeggibili. Con l'override
// gli sprite vengono RIDISEGNATI più grandi (non ingranditi), quindi restano nitidi.
let _forcedMode = '';
let _scale = 1;

export function setMarkerSizeOverride({ mode = '', scale = 1 } = {}) {
    _forcedMode = ['low', 'mid', 'full'].includes(String(mode)) ? String(mode) : '';
    const value = Number(scale);
    // Cap a 2.5: oltre, l'atlas degli sprite (~570 stazioni) esplode in memoria.
    _scale = Number.isFinite(value) ? Math.min(2.5, Math.max(1, value)) : 1;
}

export function markerZoomMode(zoom, mobile) {
    if (_forcedMode) return _forcedMode;
    if (mobile) {
        if (zoom <= 9) return 'low';
        if (zoom <= 11) return 'mid';
        return 'full';
    }
    if (zoom <= 8) return 'low';
    if (zoom <= 10) return 'mid';
    return 'full';
}

export function pickTextAndStroke(fillHex) {
    const lum = chroma(fillHex).luminance();
    const contrastBlack = (lum + 0.05) / 0.05;
    const contrastWhite = 1.05 / (lum + 0.05);
    const fill = contrastBlack >= contrastWhite ? '#050505' : '#ffffff';
    const stroke = fill === '#050505' ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.32)';
    return { fill, stroke };
}

// Heading (gradi, 0=N in senso orario) della PROVENIENZA del vento. Accetta sia
// una sigla bussola (N, NNE, …) sia un valore numerico in gradi (0–360). null
// se ignoto (port di windFromHeadingDeg v1: la sola tabella bussola faceva
// puntare a Nord tutte le stazioni che riportano gradi numerici).
export function windFromHeadingDeg(dir) {
    if (dir === null || dir === undefined) return null;
    const raw = dir.toString().trim().toUpperCase();
    const s = ({SSO:'SSW',SO:'SW',OSO:'WSW',O:'W',ONO:'WNW',NO:'NW',NNO:'NNW'})[raw] || raw;
    if (!s || s === '-') return null;
    const compass = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
        'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const idx = compass.indexOf(s);
    if (idx !== -1) return idx * 22.5;
    const deg = Number(s.replace('°', '').replace(',', '.'));
    if (Number.isFinite(deg) && deg >= 0 && deg <= 360) return ((deg % 360) + 360) % 360;
    return null;
}

// Direzione VERSO cui soffia il vento (banderuola): provenienza + 180°.
export function windToHeadingDeg(dir) {
    const from = windFromHeadingDeg(dir);
    return from === null ? null : (from + 180) % 360;
}

const windScale = (v) => window.WSPalettes.scale('vento')(v);

function parseWind(v) {
    const n = parseFloat(v);
    return Number.isFinite(n) && n >= 0 ? n : null;
}

export function getWindDirectionForParam(station, parametro = 'vento') {
    if (parametro !== 'raffica') return windFromHeadingDeg(station?.direzione);
    for (const key of ['wind_gust_direction_degrees','wind_gust_direction','rafficaDirezione','direzioneRafficaMax']) {
        const value=windFromHeadingDeg(station?.[key]);if(value!==null)return value;
    }
    return null; // Current/mean direction never stands in for a daily gust bearing.
}

export function getWindMagnitudeForParam(s, parametro) {
    if (parametro === 'vento') return parseWind(s.vento);
    if (parametro === 'raffica') return parseWind(s.raffica);
    return parseWind(s.vento);
}

export function stationExtremeBadge(rec, badgesVisible, instantVisible = true) {
    if (instantVisible && (rec?.station?.weather_sicily_instant === true || rec?.station?.data_mode === 'instant')) {
        return { text: 'INSTANT', fill: '#089fbd', width: 52 };
    }
    if (!badgesVisible) return null;
    const role = String(rec?.extremeRole || '');
    if (!role) return null;
    if (role === 'both') return { text: 'MAX/MIN', fill: '#0f172a', width: 50 };
    if (role === 'min') return { text: 'MIN', fill: '#2563eb', width: 36 };
    return { text: 'MAX', fill: '#dc2626', width: 38 };
}

export function altitudePillWidth(label, mode) {
    if (!label) return 0;
    const charW = mode === 'low' ? 4.8 : 5.1;
    return Math.max(mode === 'low' ? 45 : 48, Math.min(70, Math.ceil(String(label).length * charW + 26)));
}

function roundedRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
}

function drawBadge(ctx, x, y, badge) {
    const w = badge.width || 38;
    const h = 16;
    ctx.save();
    roundedRect(ctx, x - w / 2, y - h / 2, w, h, 8);
    ctx.fillStyle = badge.fill;
    ctx.fill();
    ctx.lineWidth = 1.4;
    ctx.strokeStyle = 'rgba(255,255,255,0.96)';
    ctx.stroke();
    ctx.font = '900 9px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#fff';
    ctx.fillText(badge.text, x, y + 3.2);
    ctx.restore();
}

function drawAltitudePill(ctx, x, y, text, mode) {
    const fontSize = mode === 'low' ? 7.8 : 8.4;
    const w = altitudePillWidth(text, mode);
    const h = mode === 'low' ? 12 : 13;
    const left = x - w / 2;
    const iconX = left + 5;

    ctx.save();
    roundedRect(ctx, left, y - h / 2, w, h, h / 2);
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.fill();
    ctx.lineWidth = 0.9;
    ctx.strokeStyle = 'rgba(15,23,42,0.18)';
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(iconX, y + 4);
    ctx.lineTo(iconX + 5, y - 4);
    ctx.lineTo(iconX + 9, y + 1.2);
    ctx.lineTo(iconX + 12, y - 2.7);
    ctx.lineTo(iconX + 17, y + 4);
    ctx.closePath();
    ctx.fillStyle = '#0f172a';
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(iconX + 3.3, y - 1.3);
    ctx.lineTo(iconX + 5, y - 4);
    ctx.lineTo(iconX + 6.9, y - 1.2);
    ctx.lineTo(iconX + 5.8, y - 1.9);
    ctx.lineTo(iconX + 4.7, y - 1.2);
    ctx.closePath();
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fill();

    ctx.font = `850 ${fontSize}px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#0f172a';
    ctx.fillText(text, iconX + 17, y + 0.4);
    ctx.restore();
}

// "Mostra direzione" SOSTITUISCE il marker (v1 stationWindOnlyEnabled +
// stationDomIcon ramo ws-wind-vane): la stazione senza dato vento non viene
// renderizzata affatto (filtro a monte in stations.js).
export function stationHasWindVane(station, parametro) {
    return getWindDirectionForParam(station,parametro) !== null &&
        getWindMagnitudeForParam(station || {}, parametro) !== null;
}

// BANDERUOLA A BAFFETTI (wind barb, convenzione meteo standard). Asta lunga SHAFT
// dal punto-stazione verso la PROVENIENZA del vento (windFromHeadingDeg); baffetti
// = velocità in nodi (bandierina 50 kt, barba piena 10 kt, mezza 5 kt). Colore =
// scala vento km/h. Ombra leggera per staccarla dallo sfondo. Calma = anello aperto.
const KMH_PER_KNOT = 1.852;
// CALMA VERA = anello aperto, e SOLO sotto questa soglia. Sopra, anche quando i
// baffetti arrotondano a zero (< 2.5 kt), la convenzione meteo standard vuole
// l'ASTA NUDA: è l'asta a rendere leggibile la direzione. Senza la distinzione
// (bug fino al 2026-07-22: calm = "nessun baffetto") le reti che quantizzano il
// vento a 1 m/s = 3.6 km/h = 1.94 kt finivano TUTTE nell'anello di calma e
// apparivano "senza direzione": 94 stazioni su 298, di cui 37 SIAS su 94.
const CALM_KMH = 1.0;
export function isCalmWind(kmh) {
    return !(Number.isFinite(kmh) && kmh >= CALM_KMH);
}
// Scompone la velocità (kt) in bandierine/barbe/mezza-barba, arrotondando a 5 kt.
// `bare` = nessun baffetto (asta nuda): 1-2 kt nella convenzione standard.
function barbUnits(knots) {
    let n = Math.round(knots / 5) * 5;
    const pennants = Math.floor(n / 50); n -= pennants * 50;
    const full = Math.floor(n / 10); n -= full * 10;
    const half = n >= 5 ? 1 : 0;
    return { pennants, full, half, bare: (pennants + full + half) === 0 };
}
export function stationWindDirectionLabel(station, parametro = 'vento') {
    const magnitude = getWindMagnitudeForParam(station || {}, parametro);
    if (magnitude === null) return '';
    if (isCalmWind(magnitude)) return 'CALMA';
    const from = getWindDirectionForParam(station,parametro);
    if (from === null) return '';
    return ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSO','SO','OSO','O','ONO','NO','NNO'][Math.floor((from + 11.25) / 22.5) % 16];
}

function drawWindDirectionCapsule(ctx, cx, cy, label, fontSize) {
    ctx.save();
    ctx.font = `750 ${fontSize}px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial`;
    const width = Math.max(20, ctx.measureText(label).width + 10);
    const height = fontSize + 5;
    roundedRect(ctx, cx - width / 2, cy - height / 2, width, height, height / 2);
    ctx.fillStyle = 'rgba(255,255,255,0.88)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(18,52,71,0.14)';
    ctx.lineWidth = 0.7;
    ctx.stroke();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#123447';
    ctx.fillText(label, cx, cy + 0.3);
    ctx.restore();
}

// One compact, upright label pairs the measured speed (km/h) with its origin.
const windValueFormatter = new Intl.NumberFormat('it-IT', {maximumFractionDigits: 1});

export function stationWindValueLabel(station, parametro = 'vento') {
    const speed = getWindMagnitudeForParam(station || {}, parametro);
    if (speed === null) return '';
    const value = windValueFormatter.format(speed);
    const origin = stationWindDirectionLabel(station, parametro);
    return `${value}${origin ? ' ' + origin : ''}`;
}

// Remove unused symmetric padding so native symbol collision checks use the
// drawn barb and label, while the station's geographic anchor stays exact.
function trimWindSprite(canvas, ctx, width, height, cx, cy, makeCanvas) {
    const ratio = ctx.getTransform().a;
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let extentX = 0, extentY = 0;
    for (let y = 0; y < pixels.height; y++) for (let x = 0; x < pixels.width; x++) {
        if (pixels.data[(y * pixels.width + x) * 4 + 3] < 8) continue;
        extentX = Math.max(extentX, Math.abs(x + .5 - cx * ratio));
        extentY = Math.max(extentY, Math.abs(y + .5 - cy * ratio));
    }
    const w = 2 * Math.ceil(extentX / ratio + 2), h = 2 * Math.ceil(extentY / ratio + 2);
    const cropped = makeCanvas(w, h);
    cropped.ctx.drawImage(canvas, (cx - w / 2) * ratio, (cy - h / 2) * ratio,
        w * ratio, h * ratio, 0, 0, w, h);
    return {canvas: cropped.canvas, w, h, ax: w / 2, ay: h / 2};
}

function buildWindBarbSprite(rec, parametro, makeCanvas, mode, opts) {
    const windMag = getWindMagnitudeForParam(rec.station, parametro); // km/h
    const fromDeg = getWindDirectionForParam(rec.station,parametro);        // provenienza
    const wcolor = windScale(Math.max(0, Math.min(windMag, 120))).hex();
    const knots = windMag / KMH_PER_KNOT;
    const u = barbUnits(knots);

    // Geometria (px CSS): asta lunga SHAFT dal punto-stazione verso la provenienza.
    const W = opts.barbValues ? 144 : mode === 'low' ? 76 : 84, H = opts.barbValues ? 96 : W, cx = W / 2, cy = H / 2;       // punto-stazione = centro
    const SHAFT = opts.barbValues ? 24 : 29, BARB = opts.barbValues ? 11 : 14, STEP = opts.barbValues ? 5.5 : 6.6;      // lunghezze baffetti e passo
    const { canvas, ctx } = makeCanvas(W, H);
    ctx.save();
    ctx.shadowColor = 'rgba(10,18,28,0.5)';
    ctx.shadowBlur = 2.2;
    ctx.shadowOffsetY = 1;
    ctx.translate(cx, cy);
    // rotate(a): l'asse −y (su) va sul bearing = provenienza (verificato: 0=N→su,
    // 90=E→destra). Così l'asta punta DA dove viene il vento.
    ctx.rotate((fromDeg - (opts.mapBearing || 0)) * Math.PI / 180);
    ctx.lineWidth = 2.6;
    ctx.lineCap = 'round';
    ctx.strokeStyle = wcolor;
    ctx.fillStyle = wcolor;

    // punto stazione: pallino pieno con bordo bianco
    ctx.beginPath();
    ctx.arc(0, 0, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.save();
    ctx.shadowColor = 'transparent';
    ctx.lineWidth = 1.8;
    ctx.strokeStyle = 'rgba(255,255,255,0.96)';
    ctx.stroke();
    ctx.restore();

    if (isCalmWind(windMag)) {
        // Calma: anello aperto attorno al punto (simbolo standard).
        ctx.save();
        ctx.shadowColor = 'transparent';
        ctx.beginPath();
        ctx.arc(0, 0, 9, 0, Math.PI * 2);
        ctx.lineWidth = 2.3;
        ctx.strokeStyle = wcolor;
        ctx.stroke();
        ctx.restore();
        ctx.restore();
        drawWindDirectionCapsule(ctx, cx, cy + (opts.barbValues ? 20 : 18), opts.barbValues ? stationWindValueLabel(rec.station, parametro) : 'CALMA', opts.barbValues ? (mode === 'low' ? 9.5 : 10.5) : (mode === 'low' ? 8.5 : 9.5));
        if (opts.barbValues) return trimWindSprite(canvas, ctx, W, H, cx, cy, makeCanvas);
        return { canvas, w: W, h: H, ax: cx, ay: cy };
    }

    // asta: dal punto (0,0) verso l'alto (provenienza) fino a (0,-SHAFT)
    const tipY = -SHAFT;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, tipY);
    ctx.stroke();

    // baffetti dalla PUNTA verso il punto: prima bandierine, poi barbe, poi mezza.
    // I baffetti escono sul lato +x (destra) inclinati verso la punta (−y).
    let pos = tipY;                          // y corrente lungo l'asta (dalla punta)
    const dx = BARB * 0.82, dy = -BARB * 0.57; // vettore baffetto (destra e verso punta)

    for (let i = 0; i < u.pennants; i++) {
        // bandierina = triangolo pieno con base sull'asta
        ctx.beginPath();
        ctx.moveTo(0, pos);
        ctx.lineTo(dx, pos + dy);
        ctx.lineTo(0, pos + STEP * 1.35);
        ctx.closePath();
        ctx.fill();
        pos += STEP * 1.45;
    }
    if (u.pennants && (u.full || u.half)) pos += STEP * 0.5; // stacco dopo le bandierine

    for (let i = 0; i < u.full; i++) {
        ctx.beginPath();
        ctx.moveTo(0, pos);
        ctx.lineTo(dx, pos + dy);
        ctx.stroke();
        pos += STEP;
    }
    if (u.half) {
        // mezza barba: se è l'UNICO elemento va rientrata di un passo dalla punta
        if (!u.pennants && !u.full) pos = tipY + STEP;
        ctx.beginPath();
        ctx.moveTo(0, pos);
        ctx.lineTo(dx * 0.5, pos + dy * 0.5);
        ctx.stroke();
    }
    ctx.restore();
    {
        const away = (fromDeg + 180 - (opts.mapBearing || 0)) * Math.PI / 180;
        if (opts.barbValues) {
            const label = stationWindValueLabel(rec.station, parametro), font = mode === 'low' ? 9.5 : 10.5;
            ctx.font = `750 ${font}px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial`;
            const labelWidth = Math.max(20, ctx.measureText(label).width + 10), labelHeight = font + 5;
            const distance = Math.abs(Math.sin(away)) * labelWidth / 2 + Math.abs(Math.cos(away)) * labelHeight / 2 + 8;
            drawWindDirectionCapsule(ctx, cx + Math.sin(away) * distance, cy - Math.cos(away) * distance, label, font);
        } else {
            drawWindDirectionCapsule(ctx, cx + Math.sin(away) * 24, cy - Math.cos(away) * 24, stationWindDirectionLabel(rec.station, parametro), 10.5);
        }
    }
    if (opts.barbValues) return trimWindSprite(canvas, ctx, W, H, cx, cy, makeCanvas);
    return { canvas, w: W, h: H, ax: cx, ay: cy };
}

// Rim arrow sits on the wind origin side and points inward; the value stays upright.
export function stationWindArrowHeading(station, parametro, bearing = 0) {
    if (!['vento','raffica'].includes(parametro) || isCalmWind(getWindMagnitudeForParam(station || {}, parametro))) return null;
    const heading = getWindDirectionForParam(station,parametro);
    if (heading === null) return null;
    return ((Math.round((heading - bearing) / 2) * 2) % 360 + 360) % 360;
}

function drawWindRimArrow(ctx, cx, cy, r, heading) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(heading * Math.PI / 180);
    ctx.beginPath();
    ctx.moveTo(0, -r - 6);
    ctx.lineTo(0, -r + 2);
    ctx.moveTo(-3.5, -r - 2);
    ctx.lineTo(0, -r + 2);
    ctx.lineTo(3.5, -r - 2);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(255,255,255,0.96)';
    ctx.lineWidth = 4;
    ctx.stroke();
    ctx.strokeStyle = '#123447';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
}

// Filled navigation arrows show the airflow destination. The upright capsule
// retains observed speed and meteorological origin, including at regional zoom.
export function stationWindFlowHeading(station, bearing = 0, parametro = 'vento') {
    const magnitude = getWindMagnitudeForParam(station || {}, parametro);
    if (magnitude === null || isCalmWind(magnitude)) return null;
    const from = getWindDirectionForParam(station,parametro);
    return from === null ? null : ((from + 180 - bearing) % 360 + 360) % 360;
}
function buildWindArrowSprite(rec, makeCanvas, mode, opts, badge, altitudeLabel, parametro) {
    const magnitude = getWindMagnitudeForParam(rec.station || {}, parametro);
    const heading = stationWindFlowHeading(rec.station, opts.mapBearing || 0, parametro);
    const origin = stationWindDirectionLabel(rec.station,parametro) || 'N/D';
    const text = `${String(rec.text || '')} · ${origin}`;
    const fontSize = mode === 'low' ? 9.5 : mode === 'mid' ? 10.5 : 11.5;
    const radius = mode === 'low' ? 14 : mode === 'mid' ? 16 : 18;
    // Text is measured once while creating the sprite, never on pointer movement.
    const measure = makeCanvas(1,1).ctx;
    measure.font = `750 ${fontSize}px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial`;
    const width = Math.ceil(Math.max(radius*2+10, measure.measureText(text).width+16, badge ? 62 : 0, altitudePillWidth(altitudeLabel,mode)));
    const badgeExtra = badge ? 18 : 0, cx = width/2, cy = badgeExtra+radius+5;
    const labelY = cy+radius+11, height = Math.ceil(labelY+fontSize/2+7+(altitudeLabel ? 15 : 0));
    const {canvas,ctx}=makeCanvas(width,height);
    if(badge)drawBadge(ctx,cx,9,badge);
    ctx.save();ctx.translate(cx,cy);
    ctx.strokeStyle='rgba(255,255,255,0.98)';ctx.lineJoin='round';ctx.lineCap='round';
    if(heading!==null){
        ctx.rotate(heading*Math.PI/180);
        ctx.beginPath();ctx.moveTo(0,-radius);ctx.lineTo(radius*.62,radius*.65);ctx.lineTo(0,radius*.30);ctx.lineTo(-radius*.62,radius*.65);ctx.closePath();
        ctx.lineWidth=5;ctx.stroke();ctx.fillStyle=rec.color;ctx.fill();ctx.lineWidth=1.25;ctx.strokeStyle='#123447';ctx.stroke();
    }else{
        // No made-up north arrow for calm or absent direction.
        ctx.beginPath();ctx.arc(0,0,isCalmWind(magnitude)?6:4,0,Math.PI*2);ctx.lineWidth=5;ctx.stroke();
        ctx.strokeStyle='#123447';ctx.lineWidth=2;ctx.stroke();
        if(!isCalmWind(magnitude)){ctx.fillStyle=rec.color;ctx.fill();}
    }
    ctx.restore();
    drawWindDirectionCapsule(ctx,cx,labelY,text,fontSize);
    if(altitudeLabel)drawAltitudePill(ctx,cx,height-7,altitudeLabel,mode);
    return {canvas,w:width,h:height,ax:cx,ay:cy};
}

// opts: { windEnabled, altitudeLabel, badgesVisible }
export function stationSpriteKey(rec, parametro, mode, opts) {
    // "Mostra direzione": la bussola sostituisce il marker → chiave dedicata
    // (niente quota/badge/testo: nel ramo vane non esistono).
    if (opts.windEnabled && stationHasWindVane(rec.station, parametro)) {
        const windMag = getWindMagnitudeForParam(rec.station, parametro);
        if (opts.barbValues) return ['barb-values-v1', mode, stationWindValueLabel(rec.station, parametro), windMag, isCalmWind(windMag) ? '' : Math.round((getWindDirectionForParam(rec.station,parametro) - (opts.mapBearing || 0)) / 2) * 2].join('|');
        // Calma: l'anello non ha direzione → chiave dedicata (altrimenti
        // condividerebbe lo sprite con l'asta nuda, che arrotonda anch'essa a 0 kt).
        // Resta la velocità (0.1 km/h) perché il colore dell'anello dipende da lei.
        if (isCalmWind(windMag)) return ['barb', 'calm', mode, Math.round(windMag * 10) / 10].join('|');
        // chiave barb: provenienza (arrot. 2°) + velocità in nodi arrotondata a 5 kt
        // (i baffetti scattano ogni 5 kt → sprite condivisi tra stazioni simili).
        return [
            'barb', mode,
            Math.round((getWindDirectionForParam(rec.station,parametro) - (opts.mapBearing || 0)) / 2) * 2,
            stationWindDirectionLabel(rec.station, parametro),
            Math.round(windMag / KMH_PER_KNOT / 5) * 5,
        ].join('|');
    }
    return [
        parametro === 'raffica' && opts.gustDirection ? 'gust-arrow-v1' : parametro === 'vento' && opts.windArrows !== false ? 'wind-arrow-v1' : parametro === 'idro' ? 'idro' : 'round',
        mode,
        rec.color,
        String(rec.text || ''),
        parametro === 'raffica' && !opts.gustDirection ? '' : stationWindArrowHeading(rec.station, parametro, opts.mapBearing || 0) ?? '',
        (parametro === 'vento' || (parametro === 'raffica' && opts.gustDirection)) ? stationWindDirectionLabel(rec.station,parametro) : '',
        opts.altitudeLabel || '',
        opts.temperatureExtreme || '',
        opts.instantVisible !== false && (rec.station?.weather_sicily_instant === true || rec.station?.data_mode === 'instant') ? 'instant' : '',
        opts.badgesVisible ? (rec.extremeRole || '') : ''
    ].join('|');
}

// Ritorna {canvas, w, h, ax, ay, dpr} in px CSS (canvas a w*dpr × h*dpr).
// Con ?markerscale=N il disegno avviene su un canvas N volte più fitto e le misure
// CSS restituite sono anch'esse ×N: il rapporto px-canvas/px-CSS resta `dpr`, quindi
// centeredImage() ridisegna 1:1 e l'icona è N volte più grande SENZA interpolazione.
export function buildStationSprite(rec, parametro, mode, opts) {
    const sprite = buildStationSpriteRaw(rec, parametro, mode, opts);
    if (_scale === 1) return sprite;
    return {
        ...sprite,
        w: sprite.w * _scale,
        h: sprite.h * _scale,
        ax: sprite.ax * _scale,
        ay: sprite.ay * _scale,
    };
}

function buildStationSpriteRaw(rec, parametro, mode, opts) {
    const dpr = stationSpriteDpr();
    const isHydro = parametro === 'idro';
    const displayText = String(rec.text || '');
    const color = rec.color;
    const { fill: textColor } = pickTextAndStroke(color);
    const badge = stationExtremeBadge(rec, opts.badgesVisible, opts.instantVisible);
    const altitudeLabel = opts.altitudeLabel || '';
    const pillW = altitudeLabel ? altitudePillWidth(altitudeLabel, mode) : 0;

    const raster = dpr * _scale;
    const makeCanvas = (w, h) => {
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(w * raster);
        canvas.height = Math.ceil(h * raster);
        // willReadFrequently: lo sprite viene subito riletto (drawImage sul
        // canvas d'appoggio CPU + getImageData) — tenerlo CPU-backed evita un
        // round-trip GPU per OGNI sprite (batch ~570 al boot, bug "a chiazze").
        const ctx = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
        ctx.setTransform(raster, 0, 0, raster, 0, 0);
        ctx.lineJoin = 'round';
        return { canvas, ctx };
    };

    // "Mostra direzione" attivo: banderuola (wind barb) AL POSTO del marker.
    if (opts.windEnabled && stationHasWindVane(rec.station, parametro)) {
        return { ...buildWindBarbSprite(rec, parametro, makeCanvas, mode, opts), dpr };
    }

    if ((parametro === 'vento' && opts.windArrows !== false) || (parametro === 'raffica' && opts.gustDirection)) return { ...buildWindArrowSprite(rec, makeCanvas, mode, opts, badge, altitudeLabel, parametro), dpr };

    if (isHydro) {
        const w0 = mode === 'full' ? 58 : (mode === 'mid' ? 52 : 48);
        const h0 = mode === 'full' ? 36 : (mode === 'mid' ? 32 : 30);
        const fontSize = mode === 'full' ? 14 : (mode === 'mid' ? 12.5 : 11.5);
        const badgeExtra = badge ? 18 : 0;
        const altitudeExtra = altitudeLabel ? (mode === 'low' ? 14 : 16) : 0;
        const iconW = Math.max(w0, badge ? 62 : 0, pillW);
        const iconH = h0 + badgeExtra + altitudeExtra;
        const x = (iconW - w0) / 2;
        const y = badgeExtra;
        const { canvas, ctx } = makeCanvas(iconW, iconH);
        if (badge) drawBadge(ctx, iconW / 2, 9, badge);
        roundedRect(ctx, x + 1.1, y + 1.1, w0 - 2.2, h0 - 2.2, 11);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = 'rgba(255,255,255,0.96)';
        ctx.stroke();
        ctx.font = `760 ${fontSize}px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = textColor;
        ctx.fillText(displayText, iconW / 2, y + h0 / 2 + 4);
        if (altitudeLabel) drawAltitudePill(ctx, iconW / 2, y + h0 + (mode === 'low' ? 7 : 8), altitudeLabel, mode);
        return { canvas, w: iconW, h: iconH, ax: iconW / 2, ay: badgeExtra + h0 / 2, dpr };
    }

    const r = mode === 'low' ? 17 : (mode === 'mid' ? 20 : 23);
    const topExtra = (parametro === 'vento' ? 10 : 3) + (badge ? 18 : 0);
    const pad = parametro === 'vento' ? 9 : 4;
    const altitudeExtra = altitudeLabel ? (mode === 'low' ? 14 : 16) : 0;
    const w = Math.max((r + pad) * 2, badge ? 62 : 0, pillW);
    const h = r * 2 + topExtra + pad + altitudeExtra;
    const cx = w / 2;
    const cy = topExtra + r;
    const fontSize = opts.readable ? (mode === 'low' ? 11.5 : mode === 'mid' ? 12.5 : 13.5) : mode === 'low' ? 10.5 : (mode === 'mid' ? 11.5 : 13);
    const { canvas, ctx } = makeCanvas(w, h);
    if (badge) drawBadge(ctx, cx, 9, badge);
    if (opts.temperatureExtreme) {
        ctx.save();
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.arc(cx, cy, r + 2, 0, Math.PI * 2);
        ctx.strokeStyle = opts.temperatureExtreme === 'max' ? '#ef3340' : '#65cfff';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.restore();
    }
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    // In 3D a single subtle outline separates overlapping markers without
    // the heavy dark/white double ring. Keep the larger readable values.
    ctx.lineWidth = opts.readable ? (mode === 'low' ? 1 : 1.2) : mode === 'low' ? 1 : 1.3;
    ctx.strokeStyle = opts.readable ? 'rgba(7,22,37,0.45)' : 'rgba(255,255,255,0.96)';
    ctx.stroke();
    ctx.font = `760 ${fontSize}px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = textColor;
    const directionLabel = (parametro === 'vento' || (parametro === 'raffica' && opts.gustDirection)) ? stationWindDirectionLabel(rec.station,parametro) : '';
    ctx.fillText(displayText, cx, cy + (directionLabel ? 0 : 4));
    if (directionLabel) drawWindDirectionCapsule(ctx, cx, cy + (mode === 'full' ? 12 : mode === 'low' ? 8 : 10), directionLabel, mode === 'low' ? (directionLabel === 'CALMA' ? 6.8 : 7.5) : directionLabel === 'CALMA' ? 8 : mode === 'full' ? 9.5 : 8.5);
    const arrowHeading = parametro === 'raffica' && !opts.gustDirection ? null : stationWindArrowHeading(rec.station, parametro, opts.mapBearing || 0);
    if (arrowHeading !== null) drawWindRimArrow(ctx, cx, cy, r, arrowHeading);
    if (altitudeLabel) drawAltitudePill(ctx, cx, cy + r + (mode === 'low' ? 7 : 8), altitudeLabel, mode);
    return { canvas, w, h, ax: cx, ay: cy, dpr };
}
